/**
 * The SIMULATED coverage assessment. It compares the seeded expectations with
 * the native policies that are actually deployed - nothing else. It is not a
 * policy engine and never claims an effective decision: the UI labels every
 * result it produces as simulated.
 */
import type {
  NativePolicy,
  NativeRole,
  ProtectionExpectation,
  ProtectionFinding,
} from "@/api/temp/AgentProtectionService";
import {
  type ConditionEntry,
  isConditionGroup,
} from "@/components/ConditionBuilder/types";

const TYPE_LABELS: Record<NativePolicy["type"], string> = {
  ROW_ACCESS: "Row Access",
  DATA_MASKING: "Data Masking",
  OBJECT_ACCESS: "Object Access",
};

const leaves = (entries: ConditionEntry[]): ConditionEntry[] =>
  entries.flatMap(entry =>
    isConditionGroup(entry) ? leaves(entry.conditions) : [entry],
  );

const normalize = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

/** The policy restricts rows to exactly the expected attribute = value. */
const hasExpectedCondition = (
  policy: NativePolicy,
  expectation: ProtectionExpectation,
) =>
  leaves(policy.conditions).some(entry => {
    if (isConditionGroup(entry)) return false;
    const attribute = entry.attribute;
    const matchesAttribute =
      normalize(attribute?.id) === expectation.condition.attribute ||
      normalize(attribute?.name) === expectation.condition.attribute;
    const value = Array.isArray(entry.value)
      ? entry.value.map(option => option.name).join(",")
      : entry.value;
    return (
      matchesAttribute &&
      normalize(entry.operator?.id) === normalize(expectation.condition.operator) &&
      normalize(value) === normalize(expectation.condition.value)
    );
  });

const targets = (policy: NativePolicy, objectId: string) =>
  policy.targetObjectIds.some(
    id => id === objectId || objectId.startsWith(`${id}.`),
  );

/** Why one policy does - or does not - satisfy one expectation. */
const verdictFor = (
  policy: NativePolicy,
  expectation: ProtectionExpectation,
  roles: NativeRole[],
): { satisfies: boolean; verdict: string } => {
  if (!targets(policy, expectation.objectId))
    return {
      satisfies: false,
      verdict: `Targets ${policy.targetObjectIds.join(", ")}, not ${expectation.objectId}.`,
    };
  if (policy.type !== expectation.policyType)
    return {
      satisfies: false,
      verdict:
        policy.type === "DATA_MASKING"
          ? "Masks columns; it does not restrict which rows are returned."
          : `${TYPE_LABELS[policy.type]} controls objects; it does not restrict rows.`,
    };
  if (!hasExpectedCondition(policy, expectation))
    return {
      satisfies: false,
      verdict: `Its row condition does not restrict ${expectation.condition.attribute} to ${expectation.condition.value}.`,
    };
  if (policy.status === "DRAFT")
    return { satisfies: false, verdict: "Matches, but is a draft - not deployed." };
  if (policy.status === "FAILED")
    return {
      satisfies: false,
      verdict: "Matches, but its last deployment failed.",
    };
  if (policy.status === "APPLYING")
    return { satisfies: false, verdict: "Matches, but is still being applied." };
  const role = roles.find(item => item.name === policy.principal?.name);
  if (!policy.principal || !role || role.stale)
    return {
      satisfies: false,
      verdict: "Matches, but its native role mapping is missing or invalid.",
    };
  return {
    satisfies: true,
    verdict: `Deployed: restricts ${expectation.objectId} to ${expectation.condition.attribute} = ${expectation.condition.value}.`,
  };
};

export const evaluateExpectations = (
  expectations: ProtectionExpectation[],
  policies: NativePolicy[],
  rolesFor: (dataSourceId: string) => NativeRole[],
): ProtectionFinding[] =>
  expectations.map(expectation => {
    const inspected = policies
      .filter(policy => policy.dataSourceId === expectation.dataSourceId)
      .map(policy => ({
        policyId: policy.id,
        ...verdictFor(policy, expectation, rolesFor(policy.dataSourceId)),
      }));
    return {
      expectationId: expectation.id,
      satisfied: inspected.some(item => item.satisfies),
      inspected,
    };
  });
